import { ErrorSummary, RightSideModal, RightSideModalTitle } from 'components';
import { IAppointmentWithCustomer } from 'models/shop/appointments';
import * as c from 'models/constants';
import * as React from 'react';
import { IStore } from 'redux/IStore';
import { Dispatch, connect } from 'react-redux';
import { reduxForm, change } from 'redux-form';
import { ITranslateProps, InjectedFormProps } from 'models/common';
import { InjectedManualBookingModalProps, InjectedQueryParams, ManualBookingScreen } from '..';
import { translate } from 'react-i18next';
import { addShopCustomerObj, getOneShopCustomer, updateShopCustomer } from 'redux/modules/shop/shopcard';
import { ICustomer } from 'models/shop/customer';
import { createDomainValueOptions, deleteURLSearchParams, readMultipleURLSearchParams } from 'helpers/common';
import { BookingInputField, BookingSelectMenu, PortalSwitchField } from 'components/FormControls';
import { Field } from 'redux-form';
import { email, maxLength, phoneNumber, required } from 'helpers/validationattributes';
import * as i18n from 'i18next';
import { domainValuesMap } from 'redux/modules/domainvalues';
import { getShop } from 'redux/modules/shop/manageshop';
import { RightSideModalFooter } from 'components/RightSideModal';
import { IManualBookingFormData } from 'models/shop/manualbooking';
import { Link } from 'react-router';

require('./style.scss');
const maxLength100 = maxLength(100);
const maxLength50 = maxLength(50);

const validateEmailPhone = (_value, values) => {
	if (values?.phoneNumber || values?.email) {
		return undefined;
	} else {
		return i18n.t('Validation_Field_EmailOrPhone');
	}
};

const validatePhonePrefix = (_value, values) => {
	if (values?.phoneNumber && !values?.callingCodeKey) {
		return i18n.t('Validation_Client_Field_Required');
	} else {
		return undefined;
	}
};

interface IOwnProps extends InjectedManualBookingModalProps {
	appointment: IAppointmentWithCustomer;
}

export interface IAddEditCustomerScreenQueryParams extends InjectedQueryParams {
	customerId?: string;
	previousScreen?: ManualBookingScreen;
}

interface IState {}

type IProps = IOwnProps &
	ReturnType<typeof stateToProps> &
	ReturnType<typeof dispatchToProps> &
	ITranslateProps &
	InjectedFormProps;

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class AddEditCustomerScreenComponent extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.formRef = React.createRef();

		this.onSubmit = this.onSubmit.bind(this);
		this.setPreviousScreen = this.setPreviousScreen.bind(this);
	}

	formRef: React.RefObject<HTMLFormElement>;

	setPreviousScreen() {
		const params: IAddEditCustomerScreenQueryParams = readMultipleURLSearchParams(['previousScreen']);
		this.props.setScreen(params.previousScreen || ManualBookingScreen.Main);
	}

	componentDidMount(): void {
		const { shopcard, domainValues, manageShop, params } = this.props;
		// check if customer data is loaded
		const customerId = params?.customerId;
		if (customerId && shopcard.selectedCustomer?.id != customerId) {
			this.props.getOneShopCustomer(customerId);
		}

		if (!manageShop && !manageShop.isFetching) {
			this.props.getShop(this.props.shopId);
		}

		if (!domainValues.languages && !domainValues.isFetchingLanguages) {
			this.props.getLanguageDomainValues();
		}

		if (!domainValues.countryCallingCodes && !domainValues.isFetchingCountryCallingCodes) {
			this.props.getCountryCallingCodeDomainValues();
		}
	}

	onSubmit(form: ICustomer) {
		const { shopId, params } = this.props;

		let submitPromise: Promise<{ error: boolean; customer?: ICustomer }>;
		if (params.customerId) {
			submitPromise = this.props.updateShopCustomer(form);
		} else {
			submitPromise = this.props.addShopCustomer({
				...form,
				shopId,
				lastOrderDate: new Date().toUTCString(),
			});
		}

		if (submitPromise) {
			submitPromise?.then(({ error, customer }) => {
				if (!error) {
					// Only addShopCustomer returns customer, because if we are editing
					// an existing customer, it should be selected allready
					if (customer?.id) {
						this.props.changeManualBookingForm('customerId', customer.id);
					}

					this.props.setScreen(ManualBookingScreen.Main);
				}
			});
		}
	}

	componentWillUnmount(): void {
		deleteURLSearchParams(['customerId', 'previousScreen']);
	}

	public render() {
		const { t, domainValues, shopcard, handleSubmit, params, manageShop, currentFormValues, change } = this.props;
		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', domainValues?.countryCallingCodes);
		const isShopSendSmsEnabled = manageShop?.shop?.isSendSMSNotificationEnabled === true ? true : false;

		return (
			<RightSideModal
				size="small"
				modalClass="add-edit-customer-screen"
				header={
					<>
						<RightSideModalTitle
							title={
								params?.customerId
									? t('ManualBookingModal_AddEditCustomer_Title_Existing')
									: t('ManualBookingModal_AddEditCustomer_Title_New')
							}
							backButtonClick={this.setPreviousScreen}
							closeModalClick={!this.props.dirty ? this.props.closeModal : this.props.startCloseModal}
						/>
					</>
				}
				footer={
					<RightSideModalFooter
						rightButtonText={
							params?.customerId
								? t('ManualBookingModal_AddEditCustomer_SaveButton_Existing')
								: t('ManualBookingModal_AddEditCustomer_SaveButton_New')
						}
						rightButtonMobileText={t('BSPortal_Common_Save')}
						rightButtonClick={() => this.formRef?.current?.requestSubmit()}
					>
						<ErrorSummary error={shopcard.error} message={shopcard.message} />
					</RightSideModalFooter>
				}
			>
				<form onSubmit={handleSubmit(this.onSubmit)} ref={this.formRef}>
					<h3 className="add-edit-customer-screen__section__title add-edit-customer-screen__section__title--no-margin-top">
						{t('ManualBookingModal_AddEditCustomer_SectionCustomerInfo_Title')}
					</h3>
					<div className="row">
						<div className="col-12">
							<Field
								name="name"
								component={BookingInputField}
								type="text"
								validate={[maxLength50, required]}
								information={[t('User_UserProfile_Label_GivenName'), ' ', t('User_UserProfile_Label_FamilyName')]}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-4">
							<Field
								name={'callingCodeKey'}
								component={BookingSelectMenu}
								type="text"
								items={callingCodes}
								withImages={false}
								multi={false}
								isRequired={true} // to remove empty value
								information={t('BSPortal_Common_Phone_Prefix')}
								validate={[validatePhonePrefix]}
							/>
						</div>
						<div className="col-8">
							<Field
								name={'phoneNumber'}
								component={BookingInputField}
								type="text"
								information={t('PhoneNumber_Label')}
								validate={[phoneNumber, validateEmailPhone]}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-12">
							<Field
								name="email"
								component={BookingInputField}
								type="text"
								validate={[maxLength100, email, validateEmailPhone]}
								onBlurAction={() => {
									// Trim before and after spaces
									if (currentFormValues?.email?.length) {
										change('email', currentFormValues.email.trim());
									}
								}}
								information={t('User_UserPreferences_Label_Email')}
							/>
						</div>
					</div>

					<h3 className="add-edit-customer-screen__section__title">
						{t('ManualBookingModal_AddEditCustomer_Reminders_Title')}
					</h3>

					{!isShopSendSmsEnabled ? (
						<p className="add-edit-customer-screen__remindersNoticeContainer">
							{t('ManualBookingModal_AddEditCustomer_Reminder_NoticeText_Part1')}{' '}
							<Link
								onClick={() => {
									deleteURLSearchParams(['customerId', 'previousScreen']);
									this.props.closeModal();
								}}
								to={`/shop/${manageShop?.shop?.id}/edit/general`}
								className="add-edit-customer-screen__remindersNoticeLink"
							>
								{t('ManualBookingModal_AddEditCustomer_Reminder_NoticeText_Part2')}
							</Link>
						</p>
					) : null}
					<Field
						name="isSMSNotificationEnabled"
						component={PortalSwitchField}
						type="checkbox"
						label={t('ManualBookingModal_AddEditCustomer_Title_SmsToggle')}
						isDisabled={!isShopSendSmsEnabled}
						showOffButtonOnDisabled={!isShopSendSmsEnabled}
					/>
					<Field
						name="isEmailNotificationEnabled"
						component={PortalSwitchField}
						type="checkbox"
						label={t('ManualBookingModal_AddEditCustomer_Title_EmailToggle')}
					/>

					{!shopcard?.selectedCustomer?.userId && (
						<>
							<h3 className="add-edit-customer-screen__section__title">
								{t('ManualBookingModal_AddEditCustomer_RemindersLanguage_Title')}
							</h3>
							<Field
								name="languageKey"
								component={BookingSelectMenu}
								parse={(value) => (value ? Number(value) : null)}
								items={domainValues?.languages}
								type="number"
								label={t('User_UserPreferences_Label_Language')}
								information={t('User_UserPreferences_Label_Language')}
								isRequired={true} // Hide empty selection, doesn't enforce required!
								searchable={false}
							/>
						</>
					)}
				</form>
			</RightSideModal>
		);
	}
}

const dispatchToProps = (dispatch: Dispatch<IStore> /*, ownProps: IOwnProps*/) => ({
	getShop: (id: string) => dispatch(getShop(id, false, true)),
	getOneShopCustomer: (customerId) => dispatch(getOneShopCustomer(customerId)),
	getCountryCallingCodeDomainValues: () => dispatch(domainValuesMap.countryCallingCodes()),
	getLanguageDomainValues: () => dispatch(domainValuesMap.languages()),
	updateShopCustomer: (newData: Partial<ICustomer>) => dispatch(updateShopCustomer(newData)),
	addShopCustomer: (customer: ICustomer) => dispatch(addShopCustomerObj(customer)),
	changeManualBookingForm: <T extends keyof IManualBookingFormData>(key: T, value: IManualBookingFormData[T]) =>
		dispatch(change(c.FORM_NAME_MANUAL_BOOKING_PLAN, key, value)),
});

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: ICustomer = null;
	if (state.form[c.FORM_NAME_CUSTOMERPROFILE] && state.form[c.FORM_NAME_CUSTOMERPROFILE].values) {
		currentFormValues = state.form[c.FORM_NAME_CUSTOMERPROFILE].values;
	} else {
		currentFormValues = state.shopcard.selectedCustomer;
	}

	let tempCallingCodeKey = state.manageShop?.shop?.callingCodeKey;

	const params: IAddEditCustomerScreenQueryParams = readMultipleURLSearchParams(['customerId', 'previousScreen']);

	// Find current office and set the shippingCallingCodeKey field to be the callingCodeKey of the office
	if (ownProps?.officeId) {
		const currentOffice = state.manageShop?.shop?.offices?.find((off) => off.id === ownProps.officeId);
		tempCallingCodeKey = currentOffice ? currentOffice.callingCodeKey : tempCallingCodeKey;
	}

	const existingCustomer = (params.customerId && state.shopcard.selectedCustomer) || {};
	const initialValues: ICustomer = {
		languageKey: state.userLanguage?.languageKey,
		isEmailNotificationEnabled: true,
		isSMSNotificationEnabled: true,
		...existingCustomer,
	};

	if (!initialValues.callingCodeKey) {
		initialValues.callingCodeKey = tempCallingCodeKey;
	}

	return {
		initialValues,
		currentFormValues,
		shopcard: state.shopcard,
		domainValues: state.domainValues,
		manageShop: state.manageShop,
		params,
	};
};

export const AddEditCustomerScreen = connect(
	stateToProps,
	dispatchToProps,
)(
	reduxForm({
		form: c.FORM_NAME_CUSTOMERPROFILE,
		enableReinitialize: true,
		destroyOnUnmount: true,
	})(AddEditCustomerScreenComponent),
);
